import React, {type Dispatch, type SetStateAction, useCallback, useEffect, useMemo} from 'react';
import {useDispatch} from 'react-redux';

import clsx from 'clsx';
import {closeDrawer, openDrawer} from 'src/modules/entitlements/components/drawer/entitlement-drawer.slice';
import {EntitlementType} from 'src/modules/entitlements/components/drawer/typedef';
import {
    MainColumnAssignedResourceCell,
    MainColumnGroupCell,
    MainColumnHeader,
} from 'src/modules/entitlements/components/main-column';
import {mainColumnCellGroupRow} from 'src/modules/entitlements/components/main-column/main-column-cell.module.styl';

import {
    Cell,
    type CellOptions,
    type Column,
    DataTable,
    type HeaderCellOptions,
    type RowsParams,
    type StopResizeParams,
    TableHeader,
} from '@pb/kit-component-data-table-v2';
import {EllipsisTextHint} from '@pb/kit-component-ellipsis-text-hint';
import {Group} from '@pb/kit-component-group';


import {PresetsPagination} from './components/presets.pagination';
import {COLUMNS, COLUMNS_DATA, HEADERS, RELATION_LEVEL_CAPTIONS} from './constants';

import type {ResourceView} from 'src/modules/entitlements/typedef';
import type {PresetRelationItem} from 'src/openapi/admin/models/PresetRelationItem';

type Props = {
    dataTableId: string,

    expandedPresetsIds: string[],
    setExpandedPresetsIds: Dispatch<SetStateAction<string[]>>,

    relations: PresetRelationItem[],
    rows: ResourceView[],
    totalCount: number,
    shortHeader: React.ReactElement,

    getColumnWidthByColumnId: (columnId: string) => number,
    onStopResize: (params: StopResizeParams) => void,
};

export const PresetsView = (props: Props) => {
    const dispatch = useDispatch();
    const {
        rows,
        relations,
        expandedPresetsIds,
        setExpandedPresetsIds,
        totalCount,
        shortHeader,
        getColumnWidthByColumnId,
        onStopResize,
        dataTableId,
    } = props;

    const openResourceDrawer = useCallback((resourceId: string) => {
        dispatch(openDrawer({entitlementType: EntitlementType.RESOURCE, entitlementId: resourceId}));
    }, [dispatch]);

    const openGrantDrawer = useCallback((grantId: string) => {
        dispatch(openDrawer({entitlementType: EntitlementType.GRANT, entitlementId: grantId}));
    }, [dispatch]);

    const openRoleDrawer = useCallback((id: string) => {
        dispatch(openDrawer({
            entitlementType: EntitlementType.ROLE,
            entitlementId: id,
        }));
    }, [dispatch]);

    const openPresetDrawer = useCallback((id: string) => {
        dispatch(openDrawer({
            entitlementType: EntitlementType.PRESET,
            entitlementId: id,
            presetRelations: relations,
        }));
    }, [relations, dispatch]);

    const renderedRows = useMemo(() => (rows
        .filter(entitlement => {
            const record = JSON.parse(entitlement.recordId);
            if (!record.resourceId && !record.roleId) {
                return true;
            } else if (record.roleId && record.resourceId) {
                return false;
            } else {
                return expandedPresetsIds.includes(record.presetId);
            }
        })
        .map(entitlement => ({
            id: entitlement.recordId,
            columnValues: {
                title: entitlement.title,
                description: entitlement.description,
                grant: entitlement.grantTitle,
                status: entitlement.statusTitle,
                restriction: entitlement.restrictionTitle,
                level: entitlement.level ? RELATION_LEVEL_CAPTIONS[entitlement.level] : '',
            },
        }))
    ), [rows, expandedPresetsIds]);

    useEffect(() => {
        return () => {
            dispatch(closeDrawer());
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div>
            <DataTable
                id={dataTableId}
                data={{
                    rows: renderedRows, columns: COLUMNS_DATA.map((column: Column) => ({
                        ...column,
                        width: getColumnWidthByColumnId(column.columnId),
                    })),
                }}
                getHeaderTopOffset={() => 160}
                onStopResize={onStopResize}
                headers={({column}: {column: Column}, cellOptions: HeaderCellOptions) => {
                    switch (column.columnId) {
                        case COLUMNS.TITLE:
                            return (
                                <Cell
                                    {...cellOptions}
                                    key={`${column.columnId}`}
                                    column={column}
                                    resizable={true}>
                                    <MainColumnHeader selection={<div/>}>
                                        {shortHeader}
                                    </MainColumnHeader>
                                </Cell>
                            );
                        default:
                            return (
                                <Cell
                                    {...cellOptions}
                                    key={`${column.columnId}`}
                                    column={column}
                                    resizable={true}>
                                    <TableHeader indent="none" className="search-header">
                                        <Group alignItems="center">
                                            <strong>{HEADERS[column.columnId]}</strong>
                                        </Group>
                                    </TableHeader>
                                </Cell>
                            );
                    }
                }}
                renderRows={(rowsParams: RowsParams, cellOptions: CellOptions) => {
                    const {column: {columnId}} = rowsParams;
                    const value = rowsParams.row.columnValues[columnId] as string;
                    const column = rowsParams.column;
                    const rowId = rowsParams.row.id;
                    const cellKey = `${rowId}-${columnId}`;

                    const record = JSON.parse(rowId as string);

                    const isPresetRow = !record.roleId && !record.resourceId;
                    const isGroupRow = record.roleId && !record.resourceId;
                    const presetId = record.presetId;

                    const cellClasses = clsx('search-result-cell', [
                        isPresetRow && mainColumnCellGroupRow,
                    ]);

                    switch (columnId) {
                        case COLUMNS.TITLE:
                            if (isPresetRow) {
                                return (
                                    <MainColumnGroupCell
                                        key={cellKey}
                                        {...{
                                            selection: false,
                                            isEmpty: record.isEmpty,
                                            isExpanded: expandedPresetsIds.includes(presetId as string),
                                            onExpand: () => setExpandedPresetsIds(
                                                prev => [...prev, presetId as string],
                                            ),
                                            onCollapse: () => setExpandedPresetsIds(
                                                prev => prev.filter(id => id !== presetId),
                                            ),
                                            cellOptions: {...cellOptions, column},
                                        }}>
                                        <EllipsisTextHint title={value}>
                                            <a onClick={() => openPresetDrawer(record.presetId)}>
                                                <strong>{value}</strong>
                                            </a>
                                        </EllipsisTextHint>
                                    </MainColumnGroupCell>
                                );
                            }

                            if (isGroupRow) {
                                return (
                                    <MainColumnAssignedResourceCell
                                        key={cellKey}
                                        {...{cellOptions: {...cellOptions, column}}}>
                                        <EllipsisTextHint title={value}>
                                            <a onClick={() => openRoleDrawer(record.roleId)}>
                                                <b>{value}</b>
                                            </a>
                                        </EllipsisTextHint>
                                    </MainColumnAssignedResourceCell>
                                );
                            }

                            return (
                                <MainColumnAssignedResourceCell
                                    key={cellKey}
                                    {...{cellOptions: {...cellOptions, column}}}>
                                    <EllipsisTextHint title={value}>
                                        <a onClick={() => openResourceDrawer(record.resourceId)}>
                                            {value}
                                        </a>
                                    </EllipsisTextHint>
                                </MainColumnAssignedResourceCell>
                            );
                        case COLUMNS.GRANT:
                            return (
                                <Cell
                                    {...cellOptions}
                                    key={cellKey}
                                    column={column}
                                    className={cellClasses}>
                                    <EllipsisTextHint title={value}>
                                        <a onClick={() => openGrantDrawer(record.grantId)}>
                                            {value}
                                        </a>
                                    </EllipsisTextHint>
                                </Cell>
                            );
                        default:
                            return (
                                <Cell
                                    {...cellOptions}
                                    key={cellKey}
                                    column={column}
                                    className={cellClasses}>
                                    <EllipsisTextHint title={value}>
                                        {value}
                                    </EllipsisTextHint>
                                </Cell>
                            );
                    }
                }}
                paginationStripe={<PresetsPagination totalCount={totalCount}/>}
            />
        </div>
    );
};
